import { FormEventHandler, useMemo, useState } from 'react'
import { FormProps, useFormRenderer } from '../contexts/Form.context'
import {
  BucketName,
  ChangeEvent,
  EcomDigitalAsset,
  FileEntryBM,
  ProductInput,
  ProductType,
} from '@bm-js/atlascloud-shared'
import { Button } from '@bm-js/atlascloud-ui'
import Input from './Input'
import { _slugify } from '@naturalcycles/js-lib/string'
import { SLUG_REGEX } from '../cnst/misc.cnst'
import FileDropzone from './FileDropzone'
import { useFetcher } from '../utils/fetcher'

const fallbackInitialValue: ProductInput = {
  title: '',
  description: '',
  priceSEK: 10,
  images: [],
  slug: '',
  type: ProductType.DIGITAL_ASSET,
}

const ProductForm = ({ onSubmit, initialValue }: FormProps<ProductInput>) => {
  const [assetsLoading, setAssetsLoading] = useState(false)
  const fetcher = useFetcher()
  const [formData, setFormData] = useState<ProductInput>({
    ...fallbackInitialValue,
    ...initialValue,
  })
  const { isLoading } = useFormRenderer()

  const slugValid = useMemo(() => {
    if (!formData.slug) return false
    return SLUG_REGEX.test(formData.slug)
  }, [formData.slug])

  const fixSlug = () => {
    setFormData({ ...formData, slug: _slugify(formData.slug) })
  }

  const handleSubmit: FormEventHandler = (e) => {
    if (!slugValid) return
    e.preventDefault()
    onSubmit({
      ...formData,
      images: formData.images.filter((image) => image !== undefined),
    })
  }

  const handleChange = (
    e: ChangeEvent<string | (FileEntryBM | undefined)[] | EcomDigitalAsset[]>
  ) => {
    const { value, name } = e.target
    const updated = { ...formData, [name]: value }

    if (name === 'title') {
      updated.slug = _slugify(value as string)
    }

    setFormData(updated)
  }

  const addImage = () => {
    const newImage = undefined
    const event = {
      target: {
        name: 'images',
        value: [...formData.images, newImage],
      },
    }
    handleChange(event)
  }

  const removeImage = (index: number) => {
    const newImages = formData.images.filter((_, i) => i !== index)
    const event = {
      target: {
        name: 'images',
        value: newImages,
      },
    }
    handleChange(event)
  }

  const updateImage = (index: number, newImage: FileEntryBM | undefined) => {
    const newImages = formData.images.map((image, i) => {
      if (i === index) {
        return newImage
      }
      return image
    })
    const event = {
      target: {
        name: 'images',
        value: newImages.filter((image) => image !== undefined),
      },
    }
    handleChange(event)
  }

  const moveImage = (from: number, to: number) => {
    if (to < 0 || to >= formData.images.length) return

    const images = [...formData.images]
    const [removed] = images.splice(from, 1)
    images.splice(to, 0, removed)
    const event = {
      target: {
        name: 'images',
        value: images,
      },
    }
    handleChange(event)
  }

  const onDigitalAssetUpload = async (uuids: string[]) => {
    setAssetsLoading(true)
    const assets = await Promise.all(
      uuids.map(async (uuid) => {
        const { err, data } = await fetcher<EcomDigitalAsset>({
          path: `files/ecom-asset/${uuid}`,
        })

        if (err || !data) throw err || new Error('No data returned')
        return data
      })
    )
    const event = {
      target: {
        name: 'digitalAssets',
        value: [...(formData.digitalAssets || []), ...assets],
      },
    }
    handleChange(event)
    setAssetsLoading(false)
  }

  const removeDigitalAsset = (uuid: string) => {
    if (assetsLoading) return
    const newAssets = (formData.digitalAssets || []).filter(
      (asset) => asset.fileEntryUuid !== uuid
    )
    const event = {
      target: {
        name: 'digitalAssets',
        value: newAssets,
      },
    }
    handleChange(event)
  }

  return (
    <form onSubmit={handleSubmit}>
      <Input
        type='text'
        desc='Produktens titel'
        name='title'
        value={formData.title}
        onChange={handleChange}
        label='Titel'
        disabled={isLoading}
        required
      />
      <Input
        type='text'
        name='slug'
        desc='Slug används för att skapa en unik URL för produkten'
        value={formData.slug}
        onChange={handleChange}
        label='Slug'
        disabled={isLoading}
      />
      {!slugValid && (
        <div className='flex items-center gap-2 -mt-6 mb-2'>
          <p className='m-0  text-red-500 text-sm'>Ogiltigt format</p>
          <button
            className='text-sm text-primary-500 underline'
            onClick={fixSlug}
          >
            Formattera
          </button>
        </div>
      )}
      <Input
        type='number'
        name='priceSEK'
        desc='Produktens pris i SEK'
        value={formData.priceSEK}
        min={1}
        onChange={handleChange}
        label='Pris'
        required
        disabled={isLoading || !!initialValue?.priceSEK}
      />
      <Input
        type='textarea'
        desc='Beskrivningen visas på produktens sida'
        name='description'
        value={formData.description}
        onChange={handleChange}
        label='Beskrivning'
        required
        disabled={isLoading}
      />
      <div>
        <p className='font-medium font-[Inter] text-sm'>Visningsbilder</p>
        <p className='text-sm text-gray-600 mb-2'>
          Bilder som visas på produktens sida
        </p>
        {formData.images.map((image, index) => (
          <Input
            key={`image-${index}-${image?.uuid}`}
            type='image'
            value={image}
            deleteMedia={() => removeImage(index)}
            moveMedia={(diff) => moveImage(index, index + diff)}
            setter={(f) => updateImage(index, f)}
          />
        ))}
        <Button className='mb-4' onClick={addImage}>
          Lägg till bild
        </Button>
      </div>

      <hr className='my-4' />

      <div>
        <p className='font-medium font-[Inter] text-sm'>Digitala tillgångar</p>
        <p className='text-sm text-gray-600 mb-2'>
          Digitala tillgångar som kan laddas ner av köparen efter betalning
        </p>
        <FileDropzone
          allowAnyFileType
          onUploadSuccess={onDigitalAssetUpload}
          maxFileSizeOverride={500 * 1024 * 1024} // 500MB
          bucket={BucketName.ATLASCLOUD_ECOM_ASSETS}
        />
        <div className='flex flex-col gap-2'>
          {(formData.digitalAssets || []).map((asset) => (
            <div
              key={asset.fileEntryUuid}
              className='grid grid-cols-12 gap-4 items-center justify-between border border-gray-200 rounded-md p-2'
            >
              <p title={asset.name} className='text-sm truncate col-span-5'>
                {asset.name}
              </p>
              <p className='text-sm col-span-3'>{asset.type}</p>
              <div className='flex items-center gap-2 justify-self-end col-span-4'>
                <a
                  href={asset.presignedUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <Button>Ladda ner</Button>
                </a>
                <Button
                  danger
                  onClick={() => removeDigitalAsset(asset.fileEntryUuid)}
                >
                  Ta bort
                </Button>
              </div>
            </div>
          ))}
        </div>
        <hr className='my-4' />
      </div>

      <Button
        className='block ml-auto'
        type='submit'
        disabled={isLoading || !slugValid || assetsLoading}
      >
        {isLoading ? 'Sparar...' : 'Spara'}
      </Button>
    </form>
  )
}

export default ProductForm
